/**
 * The segment switch above the Einheiten list (Units redesign, Slice 5).
 *
 * CO-LIVING COMES FIRST, AND IS THE DEFAULT. It is the larger part of the
 * portfolio, so it is the tab a reader lands on. Business Apartments is second.
 * That order is a product decision, not a styling detail, which is why it lives
 * in the exported `UNIT_SEGMENTS` list rather than in JSX where a reorder would
 * look like a formatting change.
 *
 * BUILT ON THE PROJECT'S OWN TABS PRIMITIVE. `components/ui/tabs` is the
 * shadcn/Radix primitive already used by seven surfaces (invoices, calendar,
 * revenue analytics). Taking it gives roving-focus arrow keys, Home/End,
 * `role="tablist"`/`role="tab"`, `aria-selected` and the panel wiring for free —
 * none of which is worth reimplementing here.
 *
 * ITS DEFAULT LOOK IS DELIBERATELY OVERRIDDEN. The primitive ships as a pill
 * switcher: a filled `bg-muted` track with a raised active pill. This surface
 * wants the quieter underline instead, so the track becomes a bottom rule and
 * the active tab is marked by a primary-coloured border and label. `cn` runs
 * through tailwind-merge, so these overrides replace the defaults rather than
 * fighting them in the cascade.
 *
 * THE COUNTS ARE THE CALLER'S. This component does no counting and no
 * classification; it renders the numbers it is handed. The page keeps owning
 * which units belong to which segment, which matters because that population
 * still comes from the page's existing raw type comparison — see the note on
 * `UNIT_SEGMENTS` below.
 */
import React from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  SEGMENT_BUSINESS_APARTMENT,
  SEGMENT_CO_LIVING,
} from "./unitListModel";

/**
 * The two segments, in the order they are shown.
 *
 * The ids are the Slice 1 constants so the codebase keeps ONE segment
 * vocabulary. That is an identifier choice only: this slice does NOT route the
 * population through `getUnitSegment`. The page still splits its rows with the
 * raw `item.type === "Co-Living"` comparison it has always used, and the known
 * divergence between that and `normalizeUnitTypeLabel` is characterised in
 * `unitListSemantics.characterization.test.js` and left alone here on purpose.
 * Changing which units land in which tab is a list-slice decision, not a
 * navigation one.
 */
export const UNIT_SEGMENTS = [
  { id: SEGMENT_CO_LIVING, label: "Co-Living Units" },
  { id: SEGMENT_BUSINESS_APARTMENT, label: "Business Apartments" },
];

/** The segment shown on first render. */
export const DEFAULT_UNIT_SEGMENT = SEGMENT_CO_LIVING;

/**
 * The count beside a tab label.
 *
 * Quiet by default and filled when its tab is active, so the active segment
 * reads as one unit rather than a label with a detached number next to it.
 *
 * THE ACTIVE BADGE IS SOLID BECAUSE THE TINT COULD NOT CARRY ITS OWN TEXT.
 * `bg-primary/10 text-primary` measured 4.36:1 in light — under 4.5:1 for an
 * 11px figure — and raising the tint makes it worse, not better: more primary
 * in the background moves it TOWARDS the primary text. `--primary` and
 * `--primary-foreground` are a designed pair (see index.css: the foreground
 * flips with the theme so the two always work together), and used as that pair
 * the badge measures 5.88:1 in light and 6.76:1 in dark.
 */
function SegmentCount({ value, isActive }) {
  return (
    <span
      className={`ml-2 shrink-0 rounded-full px-1.5 py-px text-[11px] font-medium tabular-nums ${
        isActive
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {value}
    </span>
  );
}

/**
 * One segment's content.
 *
 * A real `TabsContent`, so the panel is associated with its tab through Radix's
 * `aria-controls`/`aria-labelledby` wiring rather than merely sitting under it.
 * Without `forceMount`, Radix renders only the active panel — which is exactly
 * the behaviour this slice wants, and is safe here because both tables are
 * purely presentational: they hold no state and run no effects or requests, so
 * unmounting one costs nothing and refetches nothing.
 */
export function UnitSegmentPanel({ segment, children }) {
  return (
    <TabsContent value={segment} className="mt-6">
      {children}
    </TabsContent>
  );
}

export default function UnitSegmentTabs({ value, onValueChange, counts, children }) {
  return (
    <Tabs value={value} onValueChange={onValueChange}>
      <TabsList
        aria-label="Segment"
        className="h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0 text-muted-foreground"
      >
        {UNIT_SEGMENTS.map((segment) => {
          const isActive = segment.id === value;
          return (
            <TabsTrigger
              key={segment.id}
              value={segment.id}
              // Equal halves on a phone so both labels stay readable side by
              // side; natural widths from `sm` up, left-aligned as in the
              // reference. The bottom border is always present and only changes
              // colour, so the row does not shift by 2px when a tab activates.
              className="min-w-0 flex-1 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-2 pt-1 text-[13px] font-medium shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none sm:flex-none sm:px-3"
            >
              <span className="truncate">{segment.label}</span>
              <SegmentCount value={counts?.[segment.id] ?? 0} isActive={isActive} />
            </TabsTrigger>
          );
        })}
      </TabsList>
      {children}
    </Tabs>
  );
}
